/**
 * Booking Service
 * API client for session availability and booking endpoints
 */

import { apiClient } from "@/lib/api-client"

export interface AvailabilitySlot {
  id: string
  date: string
  startTime: string
  endTime: string
  isBooked: boolean
}

export interface BookingResult {
  booking: {
    id: string
    status: string
    date: string
    startTime: string
    endTime: string
    ritualId: string
    ritualTitle: string
    expertId: string
    isPaid: boolean
    fee: number | null
    feeCurrency: string | null
  }
  checkoutUrl?: string
  transactionId?: string
}

export interface MyBooking {
  id: string
  status: string
  message: string | null
  bookedAt: string
  date: string | null
  startTime: string | null
  endTime: string | null
  ritual: {
    id: string
    title: string
    isPaid: boolean
    fee: number | null
    feeCurrency: string | null
    duration: string
    durationType: string
    location: string | null
    websiteLink?: string | null
    coverImage?: string | null
    media?: BookingMediaItem[]
    uploads?: Array<{
      id: string
      filePath: string
      fileName: string
      fileType: string
      thumbnailPath: string | null
      isCover: boolean
    }>
    canceledAt?: string | null
  } | null
  expert: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
    websiteUrl?: string | null
    followersCount?: number
    availableForCoaching?: boolean
  } | null
  coachingSession: {
    id: string
    title: string
    format: string
    duration: number
    participationFee: "Free" | "Paid"
    price: number | null
    feeCurrency?: string | null
    meetingLink?: string | null
    websiteLink?: string | null
    location?: string | null
    coverImage?: string | null
    media?: BookingMediaItem[]
  } | null
}

export interface BookingMediaItem {
  id: string
  uploadId: string | null
  filePath: string
  fileName: string
  fileType: string
  thumbnailPath: string | null
  mediaType: string
  isCover: boolean
}

export interface MyBookingsResponse {
  bookings: MyBooking[]
  coachingCount: number
  bookingCount: number
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}

export async function getAvailability(
  ritualId: string,
  month: number,
  year: number,
): Promise<AvailabilitySlot[]> {
  const response = await apiClient.get(`/api/bookings/availability/${ritualId}`, {
    params: { month, year },
  })
  return response.data.data
}

export async function bookSession(
  availabilityId: string,
  message?: string,
  options?: { successUrl?: string; cancelUrl?: string },
): Promise<BookingResult> {
  const response = await apiClient.post("/api/bookings/book", {
    availabilityId,
    message,
    ...(options?.successUrl ? { successUrl: options.successUrl } : {}),
    ...(options?.cancelUrl ? { cancelUrl: options.cancelUrl } : {}),
  })
  return response.data.data
}

export async function getMyBookings(
  status?: string,
  page?: number,
  limit?: number,
): Promise<MyBookingsResponse> {
  const response = await apiClient.get("/api/bookings/mine", {
    params: {
      ...(status ? { status } : {}),
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
    },
  })
  return response.data.data
}

export async function cancelBooking(bookingId: string): Promise<void> {
  await apiClient.post(`/api/bookings/${bookingId}/cancel`)
}

export async function requestTime(ritualId: string, message?: string): Promise<void> {
  await apiClient.post("/api/bookings/request-time", { ritualId, message })
}

export async function rescheduleBooking(
  bookingId: string,
  availabilityId: string,
): Promise<BookingResult> {
  const response = await apiClient.post(`/api/bookings/${bookingId}/reschedule`, {
    availabilityId,
  })
  return response.data.data
}

export interface RefundRequestStatusResponse {
  hasRequest: boolean
  status?: "PENDING" | "APPROVED" | "REJECTED" | "REFUNDED"
  requestId?: string
}

export interface RefundRequestResponse {
  id: string
  status: string
  message: string
}

export async function getRefundRequestStatus(
  ritualId: string,
): Promise<RefundRequestStatusResponse> {
  const response = await apiClient.get(`/api/payments/rituals/${ritualId}/refund-request-status`)
  return response.data.data
}

export async function submitRefundRequest(ritualId: string): Promise<RefundRequestResponse> {
  const response = await apiClient.post(`/api/payments/rituals/${ritualId}/refund-request`)
  return response.data.data
}
